import type { CSSProperties } from 'react';

/**
 * The ordinal suffix of a long date -- the "rd" in "3rd September 2026" -- is
 * set as a superscript: smaller than the date, and raised off the baseline.
 *
 * Both document chromes show a long date (the letter's date line, the site
 * instruction's DATE field), so this module owns the two ratios and both
 * surfaces read them, per the parity rules in DEVELOPMENT.md.
 *
 * Ratios rather than point sizes, because the two chromes set the date at
 * different sizes (11pt on the letter, 10pt in the SI field) and the
 * superscript has to scale with whichever it sits in.
 */

/** One long date, split so the suffix can be styled on its own. */
export interface LongDateParts {
  /** The day, as digits: "3". */
  day: string;
  /** The ordinal suffix, the part that is superscripted: "rd". */
  ordinal: string;
  /** Everything after it, leading space included: " September 2026". */
  rest: string;
}

/** Superscript size, as a fraction of the surrounding date's size. */
export const ORDINAL_SIZE_RATIO = 0.62;

/**
 * Tinos's ascender, in em (1825/2048). react-pdf places a run's baseline at
 * `ascent * fontSize` below the top of its box, so in a flex row a smaller
 * Text is ALREADY raised by `ascent * (size - ordinalSize)` with no offset
 * applied to it -- which is where the raise below comes from, and why
 * DocumentPDF sets only a font size and never a margin or a verticalAlign.
 *
 * Do not reach for react-pdf's `verticalAlign: 'super'` instead. It multiplies
 * its offset by the font's units-per-em without normalising, so on a 2048-unit
 * font like Tinos it raises by 0.82em of the run -- roughly a whole line, and
 * with no way to set the size and the raise independently. Measured, not
 * guessed: it puts an 11pt run 9.01pt above its baseline.
 */
const TINOS_ASCENT = 1825 / 2048;

/** Baseline raise, as a fraction of the surrounding date's size. */
export const ORDINAL_RAISE_RATIO = TINOS_ASCENT * (1 - ORDINAL_SIZE_RATIO);

/**
 * The same two ratios for the editor, which has to be told the raise because
 * CSS aligns baselines rather than box tops. `em` in a non-font-size property
 * resolves against the element's OWN font size, so the raise is expressed
 * relative to the superscript rather than to the date around it.
 *
 * Applied at the editor root (see DocumentEditor) so both chromes inherit them.
 */
export const DATE_ORDINAL_CSS_VARS = {
  '--date-ordinal-size': `${ORDINAL_SIZE_RATIO}em`,
  '--date-ordinal-raise': `${ORDINAL_RAISE_RATIO / ORDINAL_SIZE_RATIO}em`,
} as unknown as CSSProperties;
